import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Map, Target, Package, Users, Database,
  RefreshCw, LogOut, ChevronLeft, ChevronRight, Menu, X,
  User, Settings, ChevronsUpDown, BarChart3, Sun, Moon
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'Main',
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Planning',
    items: [
      { path: '/strategy', label: 'Strategy Hub', icon: Target },
      { path: '/releases', label: 'Releases', icon: Package },
      { path: '/capacity', label: 'Capacity', icon: BarChart3 },
    ],
  },
  {
    label: 'Administration',
    items: [
      { path: '/users', label: 'Users', icon: Users, adminOnly: true },
      { path: '/metadata', label: 'Metadata', icon: Database, adminOnly: true },
      { path: '/sync', label: 'Integrations', icon: RefreshCw },
    ],
  },
];

export function AppShell({ children, user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('upmap-theme') || 'dark');
  const userMenuRef = useRef(null);

  // Apply theme
  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
    localStorage.setItem('upmap-theme', theme);
  }, [theme]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile nav on route change
  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const displayName = user?.display_name || user?.username || user?.email?.split('@')[0] || 'User';
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-background noise-overlay" data-testid="app-shell">
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 focus-ring rounded-lg"
            data-testid="mobile-logo-home"
            aria-label="Go to dashboard"
          >
            <span className="upmap-logo">u</span>
            <span className="text-lg font-bold" style={{ color: 'hsl(82, 70%, 51%)' }}>upmap</span>
          </button>
        </div>
        <button
          onClick={() => navigate('/profile')}
          className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-sm font-bold hover:bg-primary/25 transition-colors"
          data-testid="mobile-profile-avatar"
          aria-label="Open profile"
        >
          {initials}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
      )}

      <div className="flex h-[100dvh] md:h-screen overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0
            fixed md:relative z-40 md:z-auto
            h-full
            ${collapsed ? 'w-[72px]' : 'w-[260px]'}
            bg-card/50 backdrop-blur-md border-r border-border
            flex flex-col
            transition-transform duration-200 ease-out
            md:transition-[width] md:duration-200
          `}
          data-testid="app-sidebar"
        >
          {/* Logo area - CLICKABLE */}
          <button
            onClick={() => handleNav('/')}
            className="hidden md:flex items-center gap-3 px-5 h-16 border-b border-border shrink-0 w-full hover:bg-secondary/30 transition-colors focus-ring"
            data-testid="sidebar-logo-home"
            aria-label="Go to dashboard"
          >
            <span className="upmap-logo">u</span>
            {!collapsed && (
              <span className="text-lg font-bold tracking-tight" style={{ color: 'hsl(82, 70%, 51%)' }}>upmap</span>
            )}
          </button>

          {/* Nav sections */}
          <nav className="flex-1 py-3 overflow-y-auto" data-testid="app-nav">
            {NAV_SECTIONS.map((section, si) => {
              const visibleItems = section.items.filter(
                item => !item.adminOnly || user?.role === 'admin'
              );
              if (visibleItems.length === 0) return null;

              return (
                <div key={section.label} className={si > 0 ? 'mt-4' : ''}>
                  {!collapsed && (
                    <div className="px-5 mb-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">{section.label}</span>
                    </div>
                  )}
                  {collapsed && si > 0 && <div className="mx-3 mb-2 border-t border-border" />}
                  <div className="space-y-0.5 px-3">
                    {visibleItems.map((item) => {
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNav(item.path)}
                          data-active={active}
                          className={`
                            sidebar-link relative w-full flex items-center gap-3
                            ${collapsed ? 'justify-center px-2' : 'px-3'}
                            py-2.5 rounded-lg text-sm font-medium
                            ${active
                              ? 'text-primary bg-primary/8'
                              : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                            }
                          `}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                          title={collapsed ? item.label : undefined}
                        >
                          <item.icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-primary' : ''}`} />
                          {!collapsed && <span>{item.label}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* Footer - User area */}
          <div className="border-t border-border px-3 py-3 shrink-0 space-y-1.5" ref={userMenuRef}>
            {/* User card - CLICKABLE to open menu */}
            <button
              onClick={() => collapsed ? navigate('/profile') : setUserMenuOpen(!userMenuOpen)}
              className={`w-full flex items-center gap-3 ${collapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-lg hover:bg-secondary/50 transition-colors group`}
              data-testid="sidebar-user-card"
              aria-label={collapsed ? 'Open profile' : 'User menu'}
            >
              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-sm font-bold shrink-0 group-hover:bg-primary/25 transition-colors">
                {initials}
              </div>
              {!collapsed && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
                  </div>
                  <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
                </>
              )}
            </button>

            {/* User dropdown menu */}
            {userMenuOpen && !collapsed && (
              <div className="bg-card border border-border rounded-xl shadow-xl overflow-hidden mb-1 animate-in slide-in-from-bottom-2 duration-150" data-testid="user-dropdown-menu">
                <button
                  onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors text-sm"
                  data-testid="menu-profile"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>Profile</span>
                </button>
                <div className="border-t border-border" />
                <button
                  onClick={() => { onLogout(); setUserMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-destructive/10 transition-colors text-sm text-destructive"
                  data-testid="menu-logout"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}

            {/* Theme toggle */}
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className={`w-full flex items-center gap-3 ${collapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors`}
              data-testid="theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-[18px] w-[18px] shrink-0" /> : <Moon className="h-[18px] w-[18px] shrink-0" />}
              {!collapsed && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
            </button>

            {/* Collapse toggle */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex w-full items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
              data-testid="sidebar-collapse-toggle"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /><span>Collapse</span></>}
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto md:pt-0 pt-0">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AppShell;
